/**
 * Wave 7 — Mobilization readiness from domain checklist items.
 * Prefer READY / BLOCKED / IN_PROGRESS — no invented %.
 */

export type MobilizationReadinessState = 'READY' | 'BLOCKED' | 'IN_PROGRESS';

export type MobilizationBlocker = {
  id: string;
  reason: string;
  severity: 'blocking' | 'warning';
  itemCode?: string;
};

export type MobilizationChecklistCard = {
  itemId: string;
  itemCode: string;
  title: string;
  required: boolean;
  complete: boolean;
  state: MobilizationReadinessState;
  notes?: string;
};

export type MobilizationReadinessResult = {
  overall: MobilizationReadinessState;
  cards: MobilizationChecklistCard[];
  blockers: MobilizationBlocker[];
  requiredTotal: number;
  requiredComplete: number;
  canSubmit: boolean;
  submitBlockedReason?: string;
  canActivate: boolean;
  activateBlockedReason?: string;
};

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function isTruthy(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
}

export function readinessTagColor(state: MobilizationReadinessState): string {
  switch (state) {
    case 'READY':
      return 'success';
    case 'BLOCKED':
      return 'error';
    default:
      return 'processing';
  }
}

/**
 * Build readiness from mobilization row + items (from GET detail).
 */
export function buildMobilizationReadiness(input: {
  mobilization: Record<string, unknown>;
  items?: Record<string, unknown>[];
}): MobilizationReadinessResult {
  const { mobilization } = input;
  const items = input.items || (mobilization.items as Record<string, unknown>[]) || [];
  const status = asStr(mobilization.status || 'Draft');
  const statusNorm = status.toLowerCase();
  const projectId = asStr(mobilization.project_id || mobilization.projectId);
  const contractId = asStr(mobilization.contract_id || mobilization.contractId);

  const cards: MobilizationChecklistCard[] = items.map((item) => {
    const required = isTruthy(item.is_required ?? item.isRequired ?? true);
    const complete = isTruthy(item.is_complete ?? item.isComplete);
    let state: MobilizationReadinessState = 'IN_PROGRESS';
    if (complete) state = 'READY';
    else if (required) state = 'BLOCKED';
    return {
      itemId: asStr(item.id),
      itemCode: asStr(item.item_code || item.itemCode || 'ITEM'),
      title: asStr(item.title || item.item_code || 'Checklist item'),
      required,
      complete,
      state,
      notes: asStr(item.notes) || undefined,
    };
  });

  const required = cards.filter((c) => c.required);
  const requiredComplete = required.filter((c) => c.complete).length;
  const incompleteRequired = required.filter((c) => !c.complete);

  const blockers: MobilizationBlocker[] = [];

  if (!contractId) {
    blockers.push({
      id: 'contract',
      reason: 'Mobilization must link an Active EPC Contract',
      severity: 'blocking',
    });
  }

  for (const c of incompleteRequired) {
    blockers.push({
      id: `item-${c.itemCode}`,
      reason: `Required checklist incomplete: ${c.title}`,
      severity: 'blocking',
      itemCode: c.itemCode,
    });
  }

  if (!items.length && !['activated', 'approved'].includes(statusNorm)) {
    blockers.push({
      id: 'checklist-empty',
      reason: 'No checklist items loaded — open detail to refresh',
      severity: 'warning',
    });
  }

  let overall: MobilizationReadinessState = 'IN_PROGRESS';
  if (statusNorm === 'activated' || (projectId && statusNorm === 'approved')) {
    overall = 'READY';
  } else if (incompleteRequired.length) {
    overall = 'BLOCKED';
  } else if (required.length && requiredComplete === required.length) {
    overall = statusNorm === 'approved' || statusNorm === 'submitted' ? 'READY' : 'IN_PROGRESS';
  }

  let canSubmit = false;
  let submitBlockedReason: string | undefined;
  if (!['Draft', 'Rejected'].includes(status)) {
    submitBlockedReason = `Status ${status} cannot be submitted`;
  } else if (incompleteRequired.length) {
    submitBlockedReason = `${incompleteRequired.length} required checklist item(s) incomplete`;
  } else if (!required.length && !items.length) {
    submitBlockedReason = 'Checklist not available';
  } else {
    canSubmit = true;
  }

  let canActivate = false;
  let activateBlockedReason: string | undefined;
  if (projectId || statusNorm === 'activated') {
    activateBlockedReason = 'Project already linked / activated';
  } else if (statusNorm !== 'approved') {
    activateBlockedReason = 'Mobilization must be Approved (via Approval Runtime) before activation';
  } else {
    canActivate = true;
  }

  return {
    overall,
    cards,
    blockers,
    requiredTotal: required.length,
    requiredComplete,
    canSubmit,
    submitBlockedReason,
    canActivate,
    activateBlockedReason,
  };
}
